import { CHAT_HEIGHT, CHAT_WIDTH, ChatRenderer } from "./chat-renderer";

const FRAME_MS = 1000 / 30;

export type ChatSurface = {
  canvas: HTMLCanvasElement;
  /** Advances and repaints when needed. Returns true if the canvas changed. */
  tick: (now: number) => boolean;
};

/**
 * Owns a chat canvas and throttles repaints to 30fps, and only while the
 * conversation is actually moving. With `animate: false` it paints a single
 * settled frame, for reduced motion.
 */
export function createChatSurface({
  animate,
  scale = 1,
}: {
  animate: boolean;
  scale?: number;
}): ChatSurface {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(CHAT_WIDTH * scale);
  canvas.height = Math.round(CHAT_HEIGHT * scale);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(scale, scale);

  const renderer = new ChatRenderer(ctx);
  const start = performance.now();
  renderer.seed(start, animate ? 0 : 4);
  renderer.draw(start + 10_000);

  let lastPaint = 0;
  let pendingFinalPaint = true;

  return {
    canvas,
    tick(now) {
      if (!animate) return false;
      if (now - lastPaint < FRAME_MS) return false;

      const moving = renderer.update(now);
      if (!moving && !pendingFinalPaint) return false;

      renderer.draw(now);
      lastPaint = now;
      // Paint one more frame after motion stops so the settled state lands.
      pendingFinalPaint = moving;
      return true;
    },
  };
}
